import React, { useState, useEffect } from 'react';
import type { Dispatch, SetStateAction, FormEvent } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { churchConfig as configApi } from '../../api/endpoints';
import { invalidateChurchConfig, useNotification, useChurchConfig } from '../../hooks';
import { navLayoutOf } from '../../utils/churchSettings';
import type { Notify } from '../../hooks';
import useStyles from '../../theme/useStyles';
import type { Tokens } from '../../theme/tokens';
import { useTheme, clearStoredTheme } from '../../theme/ThemeContext';
import { Notification } from '../../components/ui';
import { setTranslateCookie } from '../../components/layout/GoogleTranslateShim';
import { LANGUAGES, DEFAULT_LANGUAGE } from '../../utils/languages';
import type { LanguageName } from '../../utils/languages';
import {
  SETTINGS_SECTIONS, DEFAULT_SETTINGS_SECTION, isSettingsSection, settingsPath
} from '../../routes';
import type { SettingsSection } from '../../routes';
import type { ChurchConfig } from '../../types';

import IdentitySection from './IdentitySection';
import AppearanceSection from './AppearanceSection';
import ModulesSection from './ModulesSection';
import OptionListsSection from './OptionListsSection';
import RulesSection from './RulesSection';
import UserAccessManagement from './UserAccessManagement';
import LanguageSection from './LanguageSection';
import TemplatesSection from './TemplatesSection';
import ApprovalsSection from './ApprovalsSection';
import PublicRegistrationSection from './PublicRegistrationSection';

// Every parish-wide setting behind one screen: a left menu of sections and
// the chosen section's form on the right. All sections but two edit the one
// ChurchConfig row, held here so a Save on any of them writes the whole row.
// Dropdown Lists save each list on its own, and Users is its own module.

const EMPTY: ChurchConfig = {
  churchName: '', contactNo: '', address: '', logoBase64: '',
  themeColor: ''
};

/** What every config-editing section receives. */
export interface SettingsSectionProps {
  form: ChurchConfig;
  setForm: Dispatch<SetStateAction<ChurchConfig>>;
  saving: boolean;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  notify: Notify;
  t: Tokens;
  isDarkMode?: boolean;
}

const SettingsPage = ({ isDarkMode }: { isDarkMode?: boolean }) => {
  const styles = useStyles(isDarkMode);
  const t = styles.t;
  const navigate = useNavigate();
  const { section: sectionParam } = useParams();
  const { setBrandColor, setDarkMode } = useTheme();
  // Under the sidebar layout the sections are already listed in the app's
  // own menu, so this screen shows no menu of its own.
  const showMenu = navLayoutOf(useChurchConfig()) !== 'sidebar';

  const [form, setForm] = useState<ChurchConfig>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { notification, notify, dismiss } = useNotification();

  useEffect(() => {
    let active = true;
    configApi.get()
      // A church that has never been configured returns null, which is the
      // normal first-run state rather than an error.
      .then((data) => { if (active && data) setForm({ ...EMPTY, ...data }); })
      .catch(() => { if (active) notify('Could not load the church configuration.', 'error'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // /settings/<unknown> lands on the first section rather than a blank pane.
  if (sectionParam && !isSettingsSection(sectionParam)) {
    return <Navigate to={settingsPath()} replace />;
  }
  const section: SettingsSection = isSettingsSection(sectionParam) ? sectionParam : DEFAULT_SETTINGS_SECTION;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!form.churchName?.trim()) {
      notify('The official church name is required — set it under Church Identity.', 'error');
      return;
    }

    setSaving(true);
    try {
      // The dropdown lists are saved one at a time from their own section (and
      // from every form that adds a value); leaving them out of this save
      // means a stale copy here can never overwrite them.
      const saved = await configApi.save({ ...form, optionLists: undefined });
      // Every open document reads through the shared cache, so refresh it
      // rather than waiting for a reload.
      invalidateChurchConfig(saved || form);
      // Recolour the running app immediately instead of on next load.
      setBrandColor(form.themeColor || '');
      // The administrator who just chose the parish default should see it,
      // even if this browser had toggled the theme before.
      if (section === 'appearance' && form.defaultTheme) {
        clearStoredTheme();
        setDarkMode(form.defaultTheme === 'dark');
      }
      // Google Translate re-runs from a cookie read at page load, so the new
      // language reached this browser only at the next sign-in. Saving now
      // applies it to the running session as well — pressing Save and seeing
      // nothing change read as the setting being ignored (CHM-107).
      if (section === 'language') {
        const chosen = (form.defaultLanguage || DEFAULT_LANGUAGE) as LanguageName;
        setTranslateCookie(LANGUAGES[chosen]?.code);
        notify('Language saved. Applying it now…', 'success');
        window.setTimeout(() => window.location.reload(), 700);
        return;
      }
      notify('Settings saved.', 'success');
    } catch (err) {
      notify((err as Error).message || 'Could not save the configuration.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const sectionProps: SettingsSectionProps = {
    form, setForm, saving, onSubmit: handleSubmit, notify, t, isDarkMode
  };

  const renderSection = () => {
    if (loading) {
      return (
        <div style={{ padding: '40px', textAlign: 'center', color: t.textMuted }}>
          Loading configuration…
        </div>
      );
    }
    switch (section) {
      case 'identity': return <IdentitySection {...sectionProps} />;
      case 'appearance': return <AppearanceSection {...sectionProps} />;
      case 'modules': return <ModulesSection {...sectionProps} />;
      case 'lists': return <OptionListsSection notify={notify} t={t} />;
      case 'rules': return <RulesSection {...sectionProps} />;
      case 'templates': return <TemplatesSection {...sectionProps} />;
      case 'language': return <LanguageSection {...sectionProps} />;
      case 'registration': return <PublicRegistrationSection notify={notify} t={t} isDarkMode={isDarkMode} />;
      case 'approvals': return <ApprovalsSection notify={notify} t={t} />;
      default: return null;
    }
  };

  const current = SETTINGS_SECTIONS.find((s) => s.key === section);

  return (
    <div>
      <Notification notification={notification} onClose={dismiss} />

      <div style={{ display: 'flex', gap: t.spacing.lg, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* The left menu. Sticky so it stays in reach on the long sections. */}
        {showMenu && (
        <nav
          aria-label="Settings sections"
          style={{
            ...styles.container, flex: '0 1 260px', minWidth: '220px',
            padding: t.spacing.md, position: 'sticky', top: '86px'
          }}
        >
          <div style={{
            display: 'flex', alignItems: 'center', gap: '10px',
            padding: '6px 10px 14px', borderBottom: `1px solid ${t.border}`, marginBottom: '8px'
          }}>
            <span style={{ fontSize: '22px' }} aria-hidden="true">⚙️</span>
            <div>
              <div style={{ fontWeight: t.fontWeight.black, color: t.textMain, fontSize: t.fontSize.lg }}>Settings</div>
              <div style={{ color: t.textMuted, fontSize: t.fontSize.xs }}>Parish-wide, for every user</div>
            </div>
          </div>

          {SETTINGS_SECTIONS.map((item) => {
            const active = item.key === section;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => navigate(settingsPath(item.key))}
                aria-current={active ? 'page' : undefined}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: '10px', width: '100%',
                  textAlign: 'left', padding: '10px 12px', marginBottom: '2px', cursor: 'pointer',
                  borderRadius: t.radius.lg, border: 'none',
                  borderLeft: `4px solid ${active ? t.primary : 'transparent'}`,
                  backgroundColor: active ? t.primarySoft : 'transparent',
                  color: active ? t.textAccent : t.textMain,
                  fontWeight: active ? t.fontWeight.bold : t.fontWeight.normal,
                  fontSize: t.fontSize.md, transition: 'background-color 0.15s ease'
                }}
              >
                <span style={{ fontSize: '18px', lineHeight: 1.2 }} aria-hidden="true">{item.icon}</span>
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'block' }}>{item.label}</span>
                  <span style={{ display: 'block', color: t.textMuted, fontSize: t.fontSize.xs, fontWeight: t.fontWeight.normal, marginTop: '2px' }}>
                    {item.hint}
                  </span>
                </span>
              </button>
            );
          })}
        </nav>
        )}

        {/* The chosen section. Users brings its own card; the rest share one. */}
        <main style={{ flex: '1 1 520px', minWidth: 0 }}>
          {section === 'users' ? (
            <UserAccessManagement isDarkMode={isDarkMode} />
          ) : (
            <div style={styles.container}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: t.spacing.lg }}>
                <span style={{ fontSize: '26px' }} aria-hidden="true">{current?.icon}</span>
                <div>
                  <h2 style={{ margin: 0, color: t.textMain, fontSize: t.fontSize.xxl }}>{current?.label}</h2>
                  <div style={{ color: t.textMuted, fontSize: t.fontSize.sm }}>{current?.hint}</div>
                </div>
              </div>
              {renderSection()}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default SettingsPage;
